export const BROWSER_IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "jfif", "gif", "bmp", "webp", "avif", "ico", "svg"];

const IMAGE_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/bmp",
  "image/webp",
  "image/avif",
  "image/x-icon",
  "image/vnd.microsoft.icon",
  "image/svg+xml",
]);

export class ImageDocument {
  readonly pageCount = 1;

  private constructor(
    private objectURL: string,
    private image: HTMLImageElement,
    private sourceWidth: number,
    private sourceHeight: number,
  ) {
  }

  static async open(data: Uint8Array, mimeType = "application/octet-stream"): Promise<ImageDocument> {
    const blob = new Blob([toArrayBuffer(data)], { type: mimeType });
    const objectURL = URL.createObjectURL(blob);
    try {
      const image = await loadImage(objectURL);
      const sourceWidth = image.naturalWidth || image.width;
      const sourceHeight = image.naturalHeight || image.height;
      if (sourceWidth <= 0 || sourceHeight <= 0) {
        throw new Error("image has no renderable dimensions");
      }
      return new ImageDocument(objectURL, image, sourceWidth, sourceHeight);
    } catch (e) {
      URL.revokeObjectURL(objectURL);
      throw new Error(`failed to load image: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  close() {
    if (!this.objectURL) {
      return;
    }
    URL.revokeObjectURL(this.objectURL);
    this.objectURL = "";
  }

  renderPageFit(pageIndex: number, canvas: HTMLCanvasElement, maxWidth: number, maxHeight: number): { width: number; height: number } {
    if (pageIndex !== 0) {
      throw new Error(`invalid page ${pageIndex + 1}`);
    }

    const fitScale = Math.min(maxWidth / this.sourceWidth, maxHeight / this.sourceHeight);
    const cssScale = Number.isFinite(fitScale) && fitScale > 0 ? fitScale : 1;
    const dpr = window.devicePixelRatio || 1;
    const bitmapWidth = Math.max(1, Math.floor(this.sourceWidth * cssScale * dpr));
    const bitmapHeight = Math.max(1, Math.floor(this.sourceHeight * cssScale * dpr));
    const cssWidth = Math.max(1, Math.floor(bitmapWidth / dpr));
    const cssHeight = Math.max(1, Math.floor(bitmapHeight / dpr));

    canvas.width = bitmapWidth;
    canvas.height = bitmapHeight;
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("failed to create canvas context");
    }
    ctx.clearRect(0, 0, bitmapWidth, bitmapHeight);
    ctx.drawImage(this.image, 0, 0, bitmapWidth, bitmapHeight);
    return { width: cssWidth, height: cssHeight };
  }
}

export function isBrowserImageFileName(fileName: string): boolean {
  const ext = extensionFromFileName(fileName);
  return BROWSER_IMAGE_EXTENSIONS.includes(ext);
}

export function isBrowserImageMimeType(mimeType: string): boolean {
  return IMAGE_MIME_TYPES.has(mimeType.toLowerCase());
}

export function imageMimeTypeFromFileName(fileName: string, fallback = "application/octet-stream"): string {
  switch (extensionFromFileName(fileName)) {
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
    case "jfif":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "bmp":
      return "image/bmp";
    case "webp":
      return "image/webp";
    case "avif":
      return "image/avif";
    case "ico":
      return "image/x-icon";
    case "svg":
      return "image/svg+xml";
    default:
      return fallback;
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("browser image decoder rejected the file"));
    image.src = src;
  });
}

function extensionFromFileName(fileName: string): string {
  const match = /\.([A-Za-z0-9]+)$/.exec(fileName);
  return match ? match[1].toLowerCase() : "";
}

function toArrayBuffer(data: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(data.byteLength);
  copy.set(data);
  return copy.buffer;
}
